import React, { ErrorInfo } from 'react'
import { Store } from 'redux'
import { getLogger } from '../../../../services/logger/logger'
import ErrorPage from '../../../pages/errorPage/ErrorPage'
import { BUILD_NUMBER } from '../../../../config'
import { InitState } from '../../../../redux/reducers/root/initialState'

type ErrorBoundaryProps = React.PropsWithChildren<{
  store?: Store<InitState>
}>

type FrontendCrashContext = {
  buildNumber: string
  location: {
    href: string
    pathname: string
    search: string
  }
  flow: {
    name?: string
    path?: string
    owner?: string
    offerType?: string
    estateType?: string
    isMobileFlow?: boolean
  }
  identifiers: {
    listingId?: string
    realEstateId?: number
    progressState?: string
    entryMode?: string
    externalIdPresent: boolean
  }
  user: {
    hasSsoId: boolean
    contingentCustomer: boolean
    isProfessional: boolean
  }
  ui: {
    errorPage?: string
    replaceHistory?: boolean
    previewModalIsOpen: boolean
    hasGlobalMessage: boolean
    hasMultimediaMessage: boolean
    hasValidGeoCoordinates: boolean
    objectChooserDisplay?: string
    collapsedScreens: string[]
  }
}

const getLocationDetails = () => ({
  href: window.location.href,
  pathname: window.location.pathname,
  search: window.location.search,
})

export const buildFrontendCrashContext = (state?: InitState): FrontendCrashContext => {
  const collapsedScreens = state?.formStateFeature.collapsedScreens
    ? Object.entries(state.formStateFeature.collapsedScreens)
        .filter(([, collapsed]) => collapsed)
        .map(([screenName]) => screenName)
    : []

  return {
    buildNumber: BUILD_NUMBER,
    location: getLocationDetails(),
    flow: {
      name: state?.flowFeature.flowInformation.name,
      path: state?.flowFeature.flowInformation.path,
      owner: state?.flowFeature.flowInformation.owner,
      offerType: state?.flowFeature.flowInformation.offerType,
      estateType: state?.flowFeature.flowInformation.estateType,
      isMobileFlow: state?.flowFeature.flowInformation.isMobileFlow,
    },
    identifiers: {
      listingId: state?.insertionFeature.insertionIdentification.id,
      realEstateId: state?.insertionFeature.insertionIdentification.realEstateId,
      progressState: state?.insertionFeature.insertionIdentification.progressState,
      entryMode: state?.insertionFeature.insertionIdentification.metaInformation?.entryMode,
      externalIdPresent: !!state?.insertionFeature.insertionData.externalId,
    },
    user: {
      hasSsoId: !!state?.userFeature.ssoId,
      contingentCustomer: state?.userFeature.contingentCustomer ?? false,
      isProfessional: state?.userFeature.isProfessional ?? false,
    },
    ui: {
      errorPage: state?.errorHandlerFeature.errorPage,
      replaceHistory: state?.errorHandlerFeature.replaceHistory,
      previewModalIsOpen: state?.previewFeature.previewModalIsOpen ?? false,
      hasGlobalMessage: !!state?.messageFeature.message,
      hasMultimediaMessage: !!state?.messageFeature.multimediaMessage,
      hasValidGeoCoordinates: state?.addressFeature.hasValidGeoCoordinates ?? false,
      objectChooserDisplay: state?.flowFeature.objectChooserInformation.displayObjectChooser,
      collapsedScreens,
    },
  }
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, { hasError: boolean }> {
  public state = { hasError: false }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ hasError: true })
    // the logger name is also used in CLB backend to configure the opsgenie alerting!
    getLogger('ErrorPage').error('error page shown', {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      errorInfo: {
        componentStack: errorInfo.componentStack,
      },
      context: buildFrontendCrashContext(this.props.store?.getState()),
    })
  }

  public render() {
    if (this.state.hasError) {
      return <ErrorPage />
    }

    return this.props.children
  }
}

export default ErrorBoundary
